import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity as ActivityIcon, Loader2 } from 'lucide-react';
import { crmNotesAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';

/**
 * ActivityFeed — system-generated CRM events for a subject. NEVER exposes
 * note body — only actor / event type / timestamp / permitted link.
 */
export function ActivityFeed({ subjectType, subjectId, limit = 25 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subjectType || !subjectId) return;
    let cancelled = false;
    setLoading(true);
    crmNotesAPI.activity({ subject_type: subjectType, subject_id: subjectId, page_size: limit })
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [subjectType, subjectId, limit]);

  return (
    <Card data-testid="activity-feed">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ActivityIcon size={14} className="text-slate-500" /> CRM Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-xs text-slate-500">No CRM activity yet.</div>
        ) : (
          <ul className="space-y-2">
            {items.map((a) => (
              <li key={a.activity_id} className="text-xs flex items-start gap-2"
                  data-testid={`activity-item-${a.activity_id}`}>
                <span className="text-slate-400 shrink-0 w-24">{formatDate(a.created_at)}</span>
                <span className="text-slate-800">{a.text}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default ActivityFeed;
